import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { DataProvider } from './context/DataContext';

// Auth Components
import ProtectedRoute from './components/routing/ProtectedRoute';
import RoleRoute from './components/routing/RoleRoute';
import ScrollToTop from './components/routing/ScrollToTop';

// Public Pages
import HomePage from './pages/home/HomePage';
import CategoryPage from './pages/category/CategoryPage';
import SubcategoryPage from './pages/subcategory/SubcategoryPage';
import TrendingPage from './pages/trending/TrendingPage';
import BrandsPage from './pages/brands/BrandsPage';
import BrandDetailPage from './pages/brands/BrandDetailPage';
import AllProductsPage from './pages/products/AllProductsPage';
import ProductDetailPage from './pages/product/ProductDetailPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';

// Admin Pages
import DashboardPage from './pages/admin/DashboardPage';
import ProfilePage from './pages/admin/ProfilePage';

// Categories
import CategoriesPage from './pages/admin/categories/CategoriesPage';
import CategoriesFormPage from './pages/admin/categories/CategoriesFormPage';

// Subcategories
import SubcategoriesPage from './pages/admin/subcategories/SubcategoriesPage';
import SubcategoriesFormPage from './pages/admin/subcategories/SubcategoriesFormPage';

// Tags
import TagsPage from './pages/admin/tags/TagsPage';
import TagsFormPage from './pages/admin/tags/TagsFormPage';

// Products
import ProductsPage from './pages/admin/products/ProductsPage';
import ProductsFormPage from './pages/admin/products/ProductsFormPage';

// Users
import UsersPage from './pages/admin/users/UsersPage';
import UserFormPage from './pages/admin/users/UsersFormPage';

// Content Management
import ContentPage from './pages/admin/content/ContentPage';
import ContentFormPage from './pages/admin/content/ContentFormPage';

// Reviews Management
import CommunityReviewsPage from './pages/admin/reviews/CommunityReviewsPage';

// Legal and Info Pages
import AboutPage from './pages/about/AboutPage.jsx';
// import HowWeChoosePage from './pages/how-we-choose/HowWeChoosePage.jsx'; // Hidden until product ratings are streamlined
import BlogIndexPage from './pages/blog/BlogIndexPage.jsx';
import BlogPostPage from './pages/blog/BlogPostPage.jsx';
import LegalHubPage from './pages/legal/LegalHubPage.jsx';
import PrivacyPage from './pages/privacy/PrivacyPage.jsx';
import TermsPage from './pages/terms/TermsPage.jsx';
import AffiliateDisclosurePage from './pages/affiliate-disclosure/AffiliateDisclosurePage.jsx';
import CookiesPage from './pages/cookies/CookiesPage.jsx';
import AccessibilityPage from './pages/accessibility/AccessibilityPage.jsx';
import MedicalDisclaimerPage from './pages/medical-disclaimer/MedicalDisclaimerPage.jsx';
import EditorialStandardsPage from './pages/editorial-standards/EditorialStandardsPage.jsx';
import AdvertisePage from './pages/advertise/AdvertisePage.jsx';
import ContactUsPage from './pages/contact/ContactUsPage.jsx';

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <ScrollToTop />
          <ToastContainer position="top-right" autoClose={3000} />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/new-in" element={<HomePage />} />
            <Route path="/trending" element={<HomePage />} />

            {/* Trending Routes */}
            <Route path="/trending/:trendingType" element={<TrendingPage />} />

            {/* All Products Route */}
            <Route path="/products" element={<AllProductsPage />} />

            {/* Brands Routes - Redirect to All Products page */}
            <Route path="/brands" element={<Navigate to="/products" replace />} />
            <Route path="/brands/:brandSlug" element={<Navigate to="/products" replace />} />

            {/* Product Routes */}
            <Route path="/product/:productSlug" element={<ProductDetailPage />} />

            {/* Category Routes */}
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/category/:categorySlug/:subcategorySlug" element={<SubcategoryPage />} />

            {/* Static category routes to ensure compatibility */}
            <Route path="/skincare" element={<Navigate to="/category/skincare" replace />} />
            <Route path="/hair" element={<Navigate to="/category/hair" replace />} />
            <Route path="/makeup" element={<Navigate to="/category/makeup" replace />} />
            <Route path="/body" element={<Navigate to="/category/body" replace />} />
            <Route path="/wellbeing" element={<Navigate to="/category/wellbeing" replace />} />
            <Route path="/health" element={<Navigate to="/category/health" replace />} />
            <Route path="/category/brands" element={<Navigate to="/brands" replace />} />

            <Route path="/signin" element={<LoginPage />} />
            <Route path="/join" element={<LoginPage />} />

            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Legal and Info Routes */}
            <Route path="/about" element={<AboutPage />} />
            {/* <Route path="/how-we-choose" element={<HowWeChoosePage />} /> */} {/* Hidden until product ratings are streamlined */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/legal" element={<LegalHubPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
            <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/contact" element={<ContactUsPage />} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={<Navigate to="/admin/dashboard" replace />}
            />

            {/* Protected Routes - Require Authentication */}
            <Route element={<ProtectedRoute />}>
              {/* Admin Dashboard */}
              <Route path="/admin/dashboard" element={<DashboardPage />} />

              {/* Profile */}
              <Route path="/admin/profile" element={<ProfilePage />} />

              {/* Super Admin Only Routes */}
              <Route element={<RoleRoute allowedRoles="Super Admin" />}>
                {/* Categories */}
                <Route path="/admin/categories" element={<CategoriesPage />} />
                <Route path="/admin/categories/new" element={<CategoriesFormPage />} />
                <Route path="/admin/categories/:id/edit" element={<CategoriesFormPage />} />
                <Route path="/admin/categories/:id/view" element={<CategoriesFormPage />} />

                {/* Subcategories */}
                <Route path="/admin/subcategories" element={<SubcategoriesPage />} />
                <Route path="/admin/subcategories/new" element={<SubcategoriesFormPage />} />
                <Route path="/admin/subcategories/:id/edit" element={<SubcategoriesFormPage />} />
                <Route path="/admin/subcategories/:id/view" element={<SubcategoriesFormPage />} />

                {/* Tags */}
                <Route path="/admin/tags" element={<TagsPage />} />
                <Route path="/admin/tags/new" element={<TagsFormPage />} />
                <Route path="/admin/tags/:id/edit" element={<TagsFormPage />} />
                <Route path="/admin/tags/:id/view" element={<TagsFormPage />} />

                {/* Users Management */}
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/users/new" element={<UserFormPage />} />
                <Route path="/admin/users/:id/edit" element={<UserFormPage />} />
                <Route path="/admin/users/:id/view" element={<UserFormPage />} />

                {/* Content Management */}
                <Route path="/admin/content" element={<ContentPage />} />
                <Route path="/admin/content/new" element={<ContentFormPage />} />
                <Route path="/admin/content/:id/edit" element={<ContentFormPage />} />
                <Route path="/admin/content/:id/view" element={<ContentFormPage />} />

                {/* Reviews Management */}
                <Route path="/admin/reviews" element={<CommunityReviewsPage />} />
              </Route>

              {/* Admin and Super Admin Routes */}
              <Route element={<RoleRoute allowedRoles={['Admin', 'Super Admin']} />}>
                {/* Products */}
                <Route path="/admin/products" element={<ProductsPage />} />
                <Route path="/admin/products/new" element={<ProductsFormPage />} />
                <Route path="/admin/products/:id/edit" element={<ProductsFormPage />} />
                <Route path="/admin/products/:id/view" element={<ProductsFormPage />} />
              </Route>
            </Route>

            {/* 404 - Not Found */}
            <Route path="*" element={<div>Page not found</div>} />
          </Routes>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;